import { ExternalLinkIcon, LoaderCircleIcon } from 'lucide-react'

import { Button, ButtonGroup, Modal } from 'slab'

import { type AdpIntegrationStep } from './types'
import ClickableExternalLink from '../reusable/ClickableExternalLink'

interface AdpIntegrationModalProps {
  isOpen: boolean
  onClose: () => void
  onStartMarketplaceSubscription: () => void
  onStartOAuthAuthorization: () => void
  onStepChange: (step: AdpIntegrationStep) => void
  marketplaceUrl: string
  currentStep?: AdpIntegrationStep
}

export function AdpIntegrationModal ({
  isOpen,
  onClose,
  onStartMarketplaceSubscription,
  onStartOAuthAuthorization,
  onStepChange,
  marketplaceUrl,
  currentStep = 'info'
}: AdpIntegrationModalProps) {
  const onStepChangeAndDontClose = (step: AdpIntegrationStep) => {
    onStepChange(step)
    return false // prevent the modal from closing
  }

  const renderInfoStep = () => (
    <>
      <Modal.Body>
        <Modal.Title>
          <div className='flex justify-left mb-4'>
            <img src={RAILS_ASSET_URL('/adp_run.png')} alt='ADP Run Logo' className='h-10' />
          </div>
          ADP Run Connection Summary
        </Modal.Title>
        <Modal.Description>
          Thatch will have access to:
        </Modal.Description>
        <div className='mt-4 space-y-4'>
          <ul className='space-y-3'>
            <li className='flex items-start'>
              <div className='flex items-center h-5'>
                <input id='company' name='company' type='checkbox' className='h-4 w-4 text-gray-400 bg-gray-100 border-gray-300 rounded' checked disabled />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='company' className='font-medium text-gray-500'>Company</label>
                <p className='text-gray-400'>Read basic company data, contact information, and address details</p>
              </div>
            </li>
            <li className='flex items-start'>
              <div className='flex items-center h-5'>
                <input id='employee-directory' name='employee-directory' type='checkbox' className='h-4 w-4 text-gray-400 bg-gray-100 border-gray-300 rounded' checked disabled />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='employee-directory' className='font-medium text-gray-500'>Employee directory</label>
                <p className='text-gray-400'>Read employee directory and organization structure information</p>
              </div>
            </li>
            <li className='flex items-start'>
              <div className='flex items-center h-5'>
                <input id='individual-details' name='individual-details' type='checkbox' className='h-4 w-4 text-gray-400 bg-gray-100 border-gray-300 rounded' checked disabled />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='individual-details' className='font-medium text-gray-500'>Individual details</label>
                <p className='text-gray-400'>Read details about an individual (demographic, contact, employment data)</p>
              </div>
            </li>
            <li className='flex items-start'>
              <div className='flex items-center h-5'>
                <input id='pay-cycle' name='pay-cycle' type='checkbox' className='h-4 w-4 text-gray-400 bg-gray-100 border-gray-300 rounded' checked disabled />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='pay-cycle' className='font-medium text-gray-500'>Pay Cycle Details</label>
                <p className='text-gray-400'>Read details about payroll runs and schedules at your company</p>
              </div>
            </li>
            <li className='flex items-start'>
              <div className='flex items-center h-5'>
                <input id='benefit-management' name='benefit-management' type='checkbox' className='h-4 w-4 text-gray-400 bg-gray-100 border-gray-300 rounded' checked disabled />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='benefit-management' className='font-medium text-gray-500'>Benefit Management</label>
                <p className='text-gray-400'>Read and write benefits for your company, enroll employees onto benefits with specific deductions</p>
              </div>
            </li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Modal.Button
            key='continue'
            variant='primary'
            onClick={() => onStepChangeAndDontClose('marketplace')}
          >
            Continue
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </>
  )

  const renderMarketplaceStep = () => (
    <>
      <Modal.Body>
        <Modal.Title>Configure ADP Run Connection</Modal.Title>
        <h2 className='heading-sm font-semibold mb-2'>Step 1: Subscribe to Thatch in ADP Marketplace</h2>

        <p>
          Open the ADP Marketplace and subscribe to the Thatch app. Once you’ve completed the subscription, come back here and continue.
        </p>
        <div className='mt-4 space-y-4'>
          <div className='space-y-3'>
            <Button
              variant='primary'
              onClick={onStartMarketplaceSubscription}
              leftIcon={ExternalLinkIcon}
            >
              Open ADP Marketplace
            </Button>

            <ClickableExternalLink
              url={marketplaceUrl}
              label='Or navigate to the ADP marketplace in a new tab:'
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Modal.Button key='back' variant='secondary' onClick={() => onStepChangeAndDontClose('info')}>
            Back
          </Modal.Button>
          <Modal.Button
            key='continue'
            variant='primary'
            onClick={() => onStepChangeAndDontClose('oauth')}
          >
            I've subscribed, continue
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </>
  )

  const renderOAuthStep = () => (
    <>
      <Modal.Body>
        <Modal.Title>Authorize Thatch to Access your ADP Run Data</Modal.Title>
        <h2 className='heading-sm font-semibold mb-2'>Step 2: Authorize Access</h2>
        <Modal.Description>
          Click Authorize to link ADP Run to your Thatch account. You may be asked to log in to your ADP account to establish the connection. After authorizing, you'll be redirected to Thatch automatically.
        </Modal.Description>
        <div className='mt-4 space-y-4'>
          <Modal.Button
            key='authorize'
            variant='primary'
            onClick={() => {
              onStartOAuthAuthorization()
              return false // prevent the modal from closing
            }}
            leftIcon={ExternalLinkIcon}
          >
            Authorize with ADP
          </Modal.Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Modal.Button key='back' variant='secondary' onClick={() => onStepChangeAndDontClose('marketplace')}>
            Back
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </>
  )

  const renderLoadingStep = () => (
    <>
      <Modal.Body>
        <Modal.Title>Redirecting to ADP...</Modal.Title>
        <div className='flex flex-col items-center justify-center py-8'>
          <LoaderCircleIcon size={48} className='animate-spin text-thatch-600 mb-4' />
          <p className='text-gray-600 text-center'>
            Please wait while we redirect you to ADP for authorization.
          </p>
          <p className='text-sm text-gray-500 text-center mt-2'>
            This may take a few moments...
          </p>
        </div>
      </Modal.Body>
    </>
  )

  const renderStepContent = () => {
    switch (currentStep) {
      case 'info':
        return renderInfoStep()
      case 'marketplace':
        return renderMarketplaceStep()
      case 'oauth':
        return renderOAuthStep()
      case 'redirecting':
        return renderLoadingStep()
      default:
        return renderInfoStep()
    }
  }

  return (
    <Modal open={isOpen} onOpenChange={onClose} width='medium'>
      {renderStepContent()}
    </Modal>
  )
}
